import { IconArrowBackUp, IconAt, IconLayoutSidebarRight, IconMailOpened, IconQuestionMark } from '@tabler/icons-react'
import { RefObject } from 'react'
import { useLocation } from 'react-router'

import { getPageKeyByPath } from '~/app/routes'
import { AstraAvatar, AstraButton, AstraIcon, AstraParagraph, AstraTooltip, DATE_TEMPLATES, day, format } from '~/astra'
import { isSupportedPageKey } from '~/hooks/usePageDrawer'
import { markInboxEntryAsRead } from '~/store/di-entity.api'
import {
    COMMENTS_THREAD_LIST_INCLUDE,
    CommentsThread,
    InboxEntry,
    isCommentsThreadInboxSource,
    isScheduleThreadRef,
    PopulatedCommentsThread,
    useCommentsThread,
    useLocation as useStormLocation,
} from '~/storm'
import { trackUserEvent } from '~/utils/analytics'
import { toInboxEntryData } from '~/utils/analytics/helpers/inboxEntryData'
import { getTimeStampText } from '~/utils/getTimeStampText'
import { getInitials } from '~/utils/user'

import { LexicalEditor } from '../Commenting/lexical/LexicalEditor'
import { ThreadSourceIcon } from '../Commenting/ThreadSourceIcon'
import { FloatingHoverMenu, useFloatingHoverMenu } from '../FloatingHoverMenu'
import { useInboxEntryHandlers } from './useInboxEntryHandlers'

export const AvatarTypeIcon = ({ inboxType, grayScale = false }: { inboxType: InboxEntry['inbox_type']; grayScale?: boolean }) => {
    if (inboxType === 'user.mentioned') {
        return <AstraIcon icon={IconAt} size="small" color={grayScale ? 'text-subtle' : 'text-default'} />
    }
    if (inboxType === 'user.replied') {
        return <AstraIcon icon={IconArrowBackUp} size="small" color={grayScale ? 'text-subtle' : 'text-default'} />
    }
    return <AstraIcon icon={IconQuestionMark} size="small" color="text-brand" />
}

function LocationThreadTitle({ locationId, formattedDate }: { locationId: number; formattedDate: string }) {
    const location = useStormLocation({ where: { id: locationId } })
    const roomCode = location?.room_code.trim()
    return roomCode ? `${roomCode} ${formattedDate}` : formattedDate
}

const ThreadTitle = ({ threadRef }: { threadRef: CommentsThread['ref'] }) => {
    if (threadRef.attribute_type === 'planning_period_thread_ref') {
        return threadRef.planning_period.name
    }
    if (isScheduleThreadRef(threadRef)) {
        const scheduleDate = day(threadRef.schedule_date)
        const formattedDate = format(scheduleDate, DATE_TEMPLATES.fullWeekdayNorwegianDate)
        if (threadRef.attribute_type === 'location_schedule_thread_ref') {
            return <LocationThreadTitle locationId={threadRef.location_id} formattedDate={formattedDate} />
        }
        return formattedDate
    }
    return null
}

function HeaderContentInner({ entry, thread }: { entry: InboxEntry; thread: PopulatedCommentsThread }) {
    const sourcePath = thread.source_path

    return (
        <div className="flex items-center gap-1">
            <AstraParagraph weight="medium" variant="body">
                <ThreadTitle threadRef={thread.ref} />
            </AstraParagraph>
            <AstraParagraph weight="regular" variant="body-small" className="text-subtle">
                {getTimeStampText(entry.created_at)}
            </AstraParagraph>
            {sourcePath && <ThreadSourceIcon sourcePath={sourcePath} />}
        </div>
    )
}

function StoreThreadHeaderContent({ entry, threadId }: { entry: InboxEntry; threadId: number }) {
    const thread = useCommentsThread({ where: { id: threadId }, include: COMMENTS_THREAD_LIST_INCLUDE })
    if (!thread) return null
    return <HeaderContentInner entry={entry} thread={thread} />
}

const HeaderContent = ({ entry }: { entry: InboxEntry }) => {
    if (isCommentsThreadInboxSource(entry.source)) {
        return <HeaderContentInner entry={entry} thread={entry.source.comments_thread} />
    }
    return <StoreThreadHeaderContent entry={entry} threadId={entry.source.comments_message.thread_id} />
}

const SourceContent = ({ entry }: { entry: InboxEntry }) => {
    const username = entry.updated_by ?? ''
    let content = ''
    let introTitle = ''
    if (entry.source.attribute_type === 'comments_thread_mention_inbox_entry_source') {
        content = entry.source.comments_thread.content.raw_content
        introTitle = `${username} nevnte deg:`
    }
    if (entry.source.attribute_type === 'comments_thread_reply_inbox_entry_source') {
        content = entry.source.comments_message.content.raw_content
        introTitle = `${username} svarte:`
    }
    if (entry.source.attribute_type === 'comments_message_mention_inbox_entry_source') {
        content = entry.source.comments_message.content.raw_content
        introTitle = `${username} nevnte deg:`
    }
    return (
        <div className="text-muted">
            <span className="font-hero-medium text-default">{introTitle}</span>
            <span className="line-clamp-5">
                <LexicalEditor editable={false} initialValue={content} userInitials={getInitials(username)} />
            </span>
        </div>
    )
}

export const InboxListItem = ({ entry, onClose, listRef }: { entry: InboxEntry; onClose: () => void; listRef: RefObject<HTMLDivElement | null> }) => {
    const { context, setReference, setFloating, getReferenceProps } = useFloatingHoverMenu()

    const { openInSelectionDrawer, navigateToSource } = useInboxEntryHandlers()

    const { pathname } = useLocation()
    const pageKey = getPageKeyByPath(pathname)
    const initials = getInitials(entry.updated_by ?? '')

    const canOpenInSelectionDrawer = isSupportedPageKey(pageKey ?? '')
    return (
        <div
            tabIndex={0}
            className="flex cursor-pointer gap-3 rounded-xl p-2 hover:bg-surface-muted"
            ref={setReference}
            {...getReferenceProps({
                onClick: e => {
                    if (e.defaultPrevented) return
                    void navigateToSource(entry)
                    onClose()
                },
            })}
        >
            <div className="relative self-start">
                <AstraAvatar variant="initial" initials={initials} size="large" useColorGenerator={!!initials} />
                <span className="absolute -right-1 bottom-[-8px] rounded-full border border-focus-inner bg-neutral-subtle-rest">
                    <AvatarTypeIcon inboxType={entry.inbox_type} />
                </span>
            </div>
            <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                    {!entry.is_read && <span className="h-3 w-3 rounded-full bg-brand-strong-rest" />}
                    <HeaderContent entry={entry} />
                </div>
                <div className="flex flex-col gap-1.5">
                    <SourceContent entry={entry} />
                </div>
            </div>
            {(!entry.is_read || canOpenInSelectionDrawer) && (
                <FloatingHoverMenu floatingContext={context} setFloating={setFloating} portalRoot={listRef.current}>
                    {!entry.is_read && (
                        <AstraTooltip tooltipContent="Merk som lest">
                            <AstraButton
                                variant="ghost"
                                icon={<AstraIcon icon={IconMailOpened} />}
                                aria-label="Merk som lest"
                                onClick={async e => {
                                    e.preventDefault()
                                    await markInboxEntryAsRead(entry.id)
                                    trackUserEvent('INBOX_ENTRY_MARKED_AS_READ', toInboxEntryData(entry))
                                }}
                            />
                        </AstraTooltip>
                    )}
                    {canOpenInSelectionDrawer && (
                        <AstraTooltip tooltipContent="Åpne i sidepanel">
                            <AstraButton
                                variant="ghost"
                                icon={<AstraIcon icon={IconLayoutSidebarRight} />}
                                aria-label="Åpne i sidepanel"
                                onClick={e => {
                                    e.preventDefault()
                                    void openInSelectionDrawer(entry)
                                    trackUserEvent('INBOX_ENTRY_SOURCE_OPENED_IN_DRAWER', toInboxEntryData(entry))
                                    onClose()
                                }}
                            />
                        </AstraTooltip>
                    )}
                </FloatingHoverMenu>
            )}
        </div>
    )
}
export const InboxList = ({ entries, onClose, listRef }: { entries: InboxEntry[]; onClose: () => void; listRef: RefObject<HTMLDivElement | null> }) => (
    <div className="-ml-2 flex flex-col gap-3 overflow-hidden">
        {entries.map(entry => (
            <InboxListItem key={entry.uuid} entry={entry} onClose={onClose} listRef={listRef} />
        ))}
    </div>
)
